import type { EngagementLevel, Session } from '@app/core';
import { computeEngagementLevel } from '@app/core';
import { create } from 'zustand';

import type { SessionRepository, SessionSummary } from '../services/repositories.js';
import type { TimeProvider } from '../services/time-provider.js';

interface SessionState {
  currentSession: Session | null;
  engagementLevel: EngagementLevel;
  loading: boolean;

  init: (
    repo: SessionRepository,
    time: TimeProvider,
    hasOnboarded: boolean,
  ) => Promise<void>;
  closeSession: (repo: SessionRepository, summary: SessionSummary) => Promise<void>;
  refreshEngagement: (
    repo: SessionRepository,
    time: TimeProvider,
    hasOnboarded: boolean,
  ) => Promise<void>;
}

export const useSessionStore = create<SessionState>(set => ({
  currentSession: null,
  engagementLevel: 'new',
  loading: true,

  init: async (repo, time, hasOnboarded) => {
    const lastSession = await repo.getLast();
    const lastCloseAt = lastSession?.closedAt ?? null;
    const engagement = computeEngagementLevel(hasOnboarded, lastCloseAt, time.now());
    const session = await repo.open(engagement);
    set({ currentSession: session, engagementLevel: engagement, loading: false });
  },

  closeSession: async (repo, summary) => {
    const state = useSessionStore.getState();
    if (state.currentSession) {
      await repo.close(state.currentSession.id, summary);
    }
  },

  refreshEngagement: async (repo, time, hasOnboarded) => {
    const lastSession = await repo.getLast();
    const lastCloseAt = lastSession?.closedAt ?? null;
    const engagement = computeEngagementLevel(hasOnboarded, lastCloseAt, time.now());
    set({ engagementLevel: engagement });
  },
}));
